import { AxiosInstance } from "axios";
import { SourceType } from "model";
import { ParsedQuery } from "query-string";
import { createContext, useContext } from "react";
import { Observable, from, merge } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { formatUrl, getExtension } from "utils";

import config from "config";
import { api } from "config/urls";
import { FileUploadResult } from "pages/Company/FileUpload";

export interface CreateFileDataConnectionResponse {
  connectionId: string;
  token: string;
}

export interface FileUploadApi {
  createFileConnection: (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType
  ) => Promise<CreateFileDataConnectionResponse>;

  uploadOne: (
    companyId: string,
    connectionId: string,
    f: File
  ) => Promise<FileUploadResult>;

  successRedirect: (connectionId: string, token: string) => void;
  errorRedirect: (
    connectionId: string,
    statusCode: number,
    statusText: string
  ) => void;
}

export const makeFileUploadApi = (instance: AxiosInstance): FileUploadApi => {
  const createFileConnection = (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType
  ): Promise<CreateFileDataConnectionResponse> => {
    query["uiFlow"] = "true";

    const url = formatUrl(
      api.companies.createFileConnection,
      {
        companyId,
      },
      query
    );

    const platformKey = (() => {
      switch (sourceType) {
        case "Accounting":
          return "ukbd";
        case "Banking":
          return "trlg";
        case "Other":
          return "njmc";
        default:
          throw new Error(
            `Source type ${sourceType} has not been configured for file upload`
          );
      }
    })();

    return instance
      .post<CreateFileDataConnectionResponse>(url, {
        platformKey,
      })
      .then((r) => r.data);
  };

  const uploadOne = (
    companyId: string,
    connectionId: string,
    file: File
  ): Promise<FileUploadResult> => {
    const url = formatUrl(api.companies.uploadFile, {
      companyId,
      connectionId,
    });

    const formData = new FormData();
    formData.append("file", file);

    return instance
      .post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then(
        () =>
          ({
            file,
            result: "success",
          }) as FileUploadResult
      )
      .catch(
        () =>
          ({
            file,
            result: "error",
          }) as FileUploadResult
      );
  };

  const successRedirect = (connectionId: string, token: string): void => {
    window.location.assign(
      config.LINK_API_URL +
        formatUrl(api.connections.success, { connectionId }, { token })
    );
  };

  const errorRedirect = (
    connectionId: string,
    statusCode: number,
    statusText: string
  ): void => {
    window.location.assign(
      config.LINK_API_URL +
        formatUrl(
          api.connections.error,
          { connectionId },
          { statusCode, statusText }
        )
    );
  };

  return { createFileConnection, uploadOne, successRedirect, errorRedirect };
};

export interface FileUploadService {
  uploadFiles: (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType,
    files: File[]
  ) => Observable<FileUploadResult>;

  successRedirect: (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType
  ) => Promise<void>;
}

export const makeFileUploadService = (
  api: FileUploadApi,
  eventTracker: (eventType: string, eventProperties?: object) => void
): FileUploadService => {
  const uploadFiles = (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType,
    files: File[]
  ): Observable<FileUploadResult> => {
    const eventProperties = {
      fileCount: files.length,
      sizes: files.map((f) => f.size),
      exts: files.map((f) => getExtension(f.name)),
    };
    eventTracker("Link file upload - Upload started", eventProperties);

    const createFileConnection = from(
      api.createFileConnection(companyId, query, sourceType)
    );

    return createFileConnection.pipe(
      switchMap(({ connectionId }) => {
        const fileResults = files.map((f) =>
          from(api.uploadOne(companyId, connectionId, f))
        );

        return merge(...fileResults).pipe(
          tap({
            next: (v) => {
              if (v.result === "error") {
                eventTracker("Link file upload - File upload failed");
              }
            },
            complete: () => {
              eventTracker("Link file upload - Upload finished");
            },
          })
        );
      })
    );
  };

  const successRedirect = (
    companyId: string,
    query: ParsedQuery,
    sourceType: SourceType
  ): Promise<void> => {
    return api
      .createFileConnection(companyId, query, sourceType)
      .then(({ connectionId, token }) =>
        api.successRedirect(connectionId, token)
      );
  };

  return { uploadFiles, successRedirect };
};

export const FileUploadServiceContext = createContext<
  FileUploadService | undefined
>(undefined);

export const useFileUploadService = (): FileUploadService => {
  const s = useContext(FileUploadServiceContext);

  if (s === undefined) {
    throw new Error("FileUploadService must be initialised before use");
  }

  return s;
};
